import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Loader2, Settings2, Download, ExternalLink, RefreshCw } from "lucide-react";
import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { dataBrowserApi, projectsApi, alibabaApi, type Project } from "@/api";
import { exportToExcel } from "@/lib/export-utils";
import { MultiSelectFilter, type MultiSelectOption } from "@/components/MultiSelectFilter";
import { AlibabaCompleteness } from "@/components/AlibabaCompleteness";

// ── SERP Column definitions ──

interface ColumnDef {
  key: string;
  label: string;
  group: string;
  width?: string;
  render?: (val: unknown, row: Record<string, unknown>) => React.ReactNode;
}

const ALL_COLUMNS: ColumnDef[] = [
  // SERP
  { key: 'keyword', label: 'keyword', group: 'SERP' },
  { key: 'rank_absolute', label: 'position', group: 'SERP' },
  { key: 'domain', label: 'domain', group: 'SERP' },
  { key: 'url', label: 'URL', group: 'SERP' },
  { key: 'serp_title', label: 'title', group: 'SERP' },
  { key: 'snippet', label: 'description', group: 'SERP' },
  { key: 'item_type', label: 'SERP Type 版位類型', group: 'SERP' },
  { key: 'rank_group', label: 'group_position', group: 'SERP' },
  // HTML Parsing
  { key: 'page_title', label: 'page_title', group: 'HTML' },
  { key: 'page_description', label: 'page_description', group: 'HTML' },
  { key: 'H1', label: 'H1', group: 'HTML' },
  { key: 'H2', label: 'H2', group: 'HTML' },
  { key: 'H3', label: 'H3', group: 'HTML' },
  { key: 'H4', label: 'H4', group: 'HTML' },
  { key: 'H5', label: 'H5', group: 'HTML' },
  { key: 'H6', label: 'H6', group: 'HTML' },
  { key: 'H_ALL', label: 'H_ALL', group: 'HTML' },
  { key: 'navigation_bar', label: 'navigation_bar', group: 'HTML' },
  { key: 'navigation_bar_link', label: 'navigation_bar_link', group: 'HTML' },
  { key: 'image_ALL', label: 'image_ALL', group: 'HTML' },
  { key: 'alt_ALL', label: 'alt_ALL', group: 'HTML' },
  // SEO Scores
  { key: 'overall_score', label: 'Overall Score', group: 'SEO Scores' },
  { key: 'url_score', label: 'URL Score', group: 'SEO Scores' },
  { key: 'content_score', label: 'Content Score', group: 'SEO Scores' },
  { key: 'links_score', label: 'Links Score', group: 'SEO Scores' },
  { key: 'images_score', label: 'Images Score', group: 'SEO Scores' },
  { key: 'security_score', label: 'Security Score', group: 'SEO Scores' },
  { key: 'indexing_score', label: 'Indexing Score', group: 'SEO Scores' },
  { key: 'hreflang_score', label: 'Hreflang Score', group: 'SEO Scores' },
  { key: 'title_length', label: 'Title Length', group: 'SEO Scores' },
  { key: 'description_length', label: 'Desc Length', group: 'SEO Scores' },
  { key: 'keyword_in_title', label: 'KW in Title', group: 'SEO Scores' },
  { key: 'keyword_in_description', label: 'KW in Desc', group: 'SEO Scores' },
  { key: 'keyword_in_h1', label: 'KW in H1', group: 'SEO Scores' },
  { key: 'issues_error_count', label: 'Errors', group: 'SEO Scores' },
  { key: 'issues_warning_count', label: 'Warnings', group: 'SEO Scores' },
  { key: 'issues_notice_count', label: 'Notices', group: 'SEO Scores' },
  // Content
  { key: 'text_content', label: 'text_content', group: 'Content' },
  { key: 'html_content', label: 'html_content', group: 'Content' },
  { key: 'markdown_content', label: 'markdown_content', group: 'Content' },
  // Stats
  { key: 'word_count', label: 'Word Count', group: 'Stats' },
  { key: 'sentence_count', label: 'Sentence Count', group: 'Stats' },
  { key: 'avg_words_per_sentence', label: 'Avg Words/Sentence', group: 'Stats' },
  { key: 'scrape_time', label: 'time', group: 'Stats' },
  { key: 'error_count', label: 'error_count', group: 'Stats' },
  { key: 'url_no_parameters', label: 'URL_no_parameters', group: 'Stats' },
  { key: 'scrape_status', label: 'scrape_status', group: 'Stats' },
  // Technical
  { key: 'http_status_code', label: 'HTTP Status', group: 'Technical' },
  { key: 'response_time_ms', label: 'Response Time (ms)', group: 'Technical' },
  { key: 'html_size_bytes', label: 'HTML Size', group: 'Technical' },
  { key: 'content_size_bytes', label: 'Content Size', group: 'Technical' },
  { key: 'text_html_ratio', label: 'Text/HTML Ratio', group: 'Technical' },
  { key: 'url_depth', label: 'URL Depth', group: 'Technical' },
  // Security & Tracking
  { key: 'has_ga', label: 'GA', group: 'Security' },
  { key: 'has_gtm', label: 'GTM', group: 'Security' },
  { key: 'has_csp', label: 'CSP', group: 'Security' },
  { key: 'has_hsts', label: 'HSTS', group: 'Security' },
  { key: 'has_x_frame', label: 'X-Frame', group: 'Security' },
  { key: 'meta_robots', label: 'Robots', group: 'Security' },
  { key: 'indexable', label: 'Indexable', group: 'Security' },
  // Links
  { key: 'internal_links_count', label: 'Internal Links', group: 'Links' },
  { key: 'external_links_count', label: 'External Links', group: 'Links' },
  { key: 'empty_anchor_count', label: 'Empty Anchors', group: 'Links' },
  { key: 'nofollow_internal_count', label: 'Nofollow Internal', group: 'Links' },
];

// Helper: format H tags from h_structure_json
function formatHByLevel(hStruct: Array<{ level: number; text: string }> | null, level: number): string {
  if (!hStruct) return '';
  return hStruct.filter(h => h.level === level).map(h => `<H${level}>${h.text}`).join('\n');
}

function formatHAll(hStruct: Array<{ level: number; text: string }> | null): string {
  if (!hStruct) return '';
  return hStruct.map(h => `<H${h.level}>${h.text}`).join('\n');
}

// Helper: flatten nav tree
function formatNavBar(navTree: any[] | null): string {
  if (!navTree || navTree.length === 0) return '';
  const labels = ['Category', 'Subcategory', 'Sub-subcategory'];
  const flat: string[] = [];
  const walk = (nodes: any[], depth: number) => {
    for (const n of nodes) {
      flat.push(`${labels[Math.min(depth, 2)]}: ${n.label || ''}`);
      if (n.children?.length) walk(n.children, depth + 1);
    }
  };
  walk(navTree, 0);
  return flat.join('\n');
}

function formatNavBarLink(navTree: any[] | null): string {
  if (!navTree || navTree.length === 0) return '';
  const labels = ['Category', 'Subcategory', 'Sub-subcategory'];
  const flat: string[] = [];
  const walk = (nodes: any[], depth: number) => {
    for (const n of nodes) {
      flat.push(`${labels[Math.min(depth, 2)]}: ${n.label || ''} (${n.url || ''})`);
      if (n.children?.length) walk(n.children, depth + 1);
    }
  };
  walk(navTree, 0);
  return flat.join('\n');
}

// ── SERP DataViewer Tab ──

function SerpDataViewer() {
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set());
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(new Set());
  const [selectedItemTypes, setSelectedItemTypes] = useState<Set<string>>(new Set());
  // Default columns: ALL columns
  const [visibleCols, setVisibleCols] = useState<Set<string>>(() => new Set(ALL_COLUMNS.map(c => c.key)));

  // Fetch projects
  const { data: projects = [] } = useQuery({
    queryKey: ["dv-projects"],
    queryFn: async () => {
      const r = await projectsApi.list();
      return (r.data || []) as Project[];
    },
  });

  // Fetch full SERP data (all projects, filter client-side)
  const { data: rawResults = [], isLoading } = useQuery({
    queryKey: ["dv-serp-full"],
    queryFn: async () => {
      const r = await dataBrowserApi.serpFull({ limit: 5000 });
      return (r.data || []) as Array<Record<string, unknown>>;
    },
  });

  // Process raw results into display format
  const displayData = useMemo(() => {
    return rawResults.map((row) => {
      const hStruct = row.h_structure_json as Array<{ level: number; text: string }> | null;
      const navTree = row.nav_tree_json as any[] | null;
      const imgs = row.images_json as Array<{ src: string; alt: string }> | null;

      let urlNp = '';
      try {
        const u = new URL(row.url as string);
        urlNp = `${u.origin}${u.pathname}`;
      } catch { urlNp = row.url as string; }

      // Format scrape time from duration_ms
      const durationMs = row.scrape_duration_ms as number | null;
      const scrapeTime = durationMs ? `${(durationMs / 1000).toFixed(1)}s` : (row.scrape_time || '');

      return {
        ...row,
        keyword: row.target_keyword || '',
        H1: formatHByLevel(hStruct, 1),
        H2: formatHByLevel(hStruct, 2),
        H3: formatHByLevel(hStruct, 3),
        H4: formatHByLevel(hStruct, 4),
        H5: formatHByLevel(hStruct, 5),
        H6: formatHByLevel(hStruct, 6),
        H_ALL: formatHAll(hStruct),
        navigation_bar: formatNavBar(navTree),
        navigation_bar_link: formatNavBarLink(navTree),
        image_ALL: imgs ? JSON.stringify(imgs.map(i => i.src)) : '',
        alt_ALL: imgs ? JSON.stringify(imgs.map(i => i.alt).filter(Boolean)) : '',
        scrape_time: scrapeTime,
        url_no_parameters: urlNp,
      } as Record<string, unknown>;
    });
  }, [rawResults]);

  // Build project options with counts from data
  const projectOptions: MultiSelectOption[] = useMemo(() => {
    const counts = new Map<string, number>();
    displayData.forEach(row => {
      const pid = row.project_id as string;
      if (pid) counts.set(pid, (counts.get(pid) || 0) + 1);
    });
    return projects
      .filter(p => counts.has(p.project_id))
      .map(p => ({ value: p.project_id, label: p.name, count: counts.get(p.project_id) || 0 }));
  }, [displayData, projects]);

  // Build keyword options with counts (respecting project filter)
  const keywordOptions: MultiSelectOption[] = useMemo(() => {
    const counts = new Map<string, number>();
    const base = selectedProjects.size > 0
      ? displayData.filter(row => selectedProjects.has(row.project_id as string))
      : displayData;
    base.forEach(row => {
      const kw = row.keyword as string;
      if (kw) counts.set(kw, (counts.get(kw) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([kw, count]) => ({ value: kw, label: kw, count }));
  }, [displayData, selectedProjects]);

  // Build item_type options with counts (respecting project + keyword filters)
  const itemTypeOptions: MultiSelectOption[] = useMemo(() => {
    const counts = new Map<string, number>();
    let base = displayData;
    if (selectedProjects.size > 0) base = base.filter(row => selectedProjects.has(row.project_id as string));
    if (selectedKeywords.size > 0) base = base.filter(row => selectedKeywords.has(row.keyword as string));
    base.forEach(row => {
      const t = (row.item_type as string) || 'organic';
      counts.set(t, (counts.get(t) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([t, count]) => ({ value: t, label: t, count }));
  }, [displayData, selectedProjects, selectedKeywords]);

  // Client-side filtering — empty Set = show all
  const filteredData = useMemo(() => {
    return displayData.filter(row => {
      if (selectedProjects.size > 0 && !selectedProjects.has(row.project_id as string)) return false;
      if (selectedKeywords.size > 0 && !selectedKeywords.has(row.keyword as string)) return false;
      if (selectedItemTypes.size > 0 && !selectedItemTypes.has((row.item_type as string) || 'organic')) return false;
      return true;
    });
  }, [displayData, selectedProjects, selectedKeywords, selectedItemTypes]);

  // Column toggle
  const toggleCol = (key: string) => {
    setVisibleCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const toggleGroup = (group: string) => {
    const groupCols = ALL_COLUMNS.filter(c => c.group === group);
    const allOn = groupCols.every(c => visibleCols.has(c.key));
    setVisibleCols(prev => {
      const next = new Set(prev);
      groupCols.forEach(c => allOn ? next.delete(c.key) : next.add(c.key));
      return next;
    });
  };
  const activeColumns = ALL_COLUMNS.filter(c => visibleCols.has(c.key));

  // Export
  const handleExport = () => {
    if (filteredData.length === 0) { toast.warning("沒有資料"); return; }
    const rows = filteredData.map(row => {
      const out: Record<string, unknown> = {};
      activeColumns.forEach(col => { out[col.label] = row[col.key] ?? ''; });
      return out;
    });
    const suffix = selectedKeywords.size === 1 ? [...selectedKeywords][0] : 'data';
    exportToExcel(rows, { filename: `data_${suffix}_${new Date().toISOString().split('T')[0]}.xlsx` });
    toast.success("Excel 匯出成功");
  };

  const groups = [...new Set(ALL_COLUMNS.map(c => c.group))];

  // Truncate long cell values for display
  const truncate = (val: unknown, maxLen = 100): string => {
    if (val === null || val === undefined) return '—';
    const s = String(val);
    return s.length > maxLen ? s.slice(0, maxLen) + '...' : s;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Badge variant="secondary">{filteredData.length} 筆結果</Badge>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="h-4 w-4 mr-1.5" />
                欄位 ({activeColumns.length}/{ALL_COLUMNS.length})
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">顯示欄位</p>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-6 text-xs"
                      onClick={() => setVisibleCols(new Set(ALL_COLUMNS.map(c => c.key)))}>全選</Button>
                    <Button variant="ghost" size="sm" className="h-6 text-xs"
                      onClick={() => setVisibleCols(new Set())}>清除</Button>
                  </div>
                </div>
                <ScrollArea className="h-[360px]">
                  {groups.map(group => {
                    const groupCols = ALL_COLUMNS.filter(c => c.group === group);
                    const allOn = groupCols.every(c => visibleCols.has(c.key));
                    return (
                      <div key={group} className="mb-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Checkbox checked={allOn} onCheckedChange={() => toggleGroup(group)} id={`group-${group}`} />
                          <label htmlFor={`group-${group}`} className="text-xs font-semibold text-muted-foreground uppercase">{group}</label>
                        </div>
                        {groupCols.map(col => (
                          <div key={col.key} className="flex items-center gap-2 ml-4 py-0.5">
                            <Checkbox checked={visibleCols.has(col.key)} onCheckedChange={() => toggleCol(col.key)} id={`col-${col.key}`} />
                            <label htmlFor={`col-${col.key}`} className="text-xs">{col.label}</label>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </ScrollArea>
              </div>
            </PopoverContent>
          </Popover>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={filteredData.length === 0}>
            <Download className="h-4 w-4 mr-1.5" />
            匯出 Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <MultiSelectFilter
              label="專案"
              options={projectOptions}
              selected={selectedProjects}
              onChange={(v) => { setSelectedProjects(v); setSelectedKeywords(new Set()); }}
            />
            <MultiSelectFilter
              label="關鍵字"
              options={keywordOptions}
              selected={selectedKeywords}
              onChange={setSelectedKeywords}
            />
            <MultiSelectFilter
              label="版位類型 SERP Type"
              options={itemTypeOptions}
              selected={selectedItemTypes}
              onChange={setSelectedItemTypes}
            />
          </div>
        </CardContent>
      </Card>

      {/* Data table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {activeColumns.map(col => (
                      <TableHead key={col.key} className="whitespace-nowrap text-xs">
                        {col.label}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={activeColumns.length} className="text-center text-muted-foreground py-16">
                        {rawResults.length === 0 ? '目前資料庫尚無 SERP 資料，請先執行關鍵字爬取' : '篩選條件無符合資料，請調整篩選器'}
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {filteredData.map((row, i) => (
                    <TableRow key={i}>
                      {activeColumns.map(col => (
                        <TableCell key={col.key} className="text-xs max-w-[200px]">
                          {col.key === 'url' ? (
                            <a href={row.url as string} target="_blank" rel="noopener noreferrer"
                              className="text-primary hover:underline flex items-center gap-1 font-mono">
                              <span className="truncate max-w-[160px]">{row.url as string}</span>
                              <ExternalLink className="h-3 w-3 flex-shrink-0" />
                            </a>
                          ) : col.key === 'item_type' ? (
                            (() => {
                              const typeColors: Record<string, string> = {
                                organic: 'bg-blue-500',
                                featured_snippet: 'bg-purple-500',
                                people_also_ask: 'bg-green-500',
                                knowledge_graph: 'bg-yellow-500',
                                local_pack: 'bg-red-500',
                                video: 'bg-pink-500',
                                ai_overview: 'bg-indigo-500',
                                paid: 'bg-orange-500',
                              };
                              const t = (row.item_type as string) || 'organic';
                              return <Badge className={`${typeColors[t] || 'bg-gray-500'} text-white text-[10px]`}>{t}</Badge>;
                            })()
                          ) : col.key === 'rank_absolute' ? (
                            <span className={`font-bold ${
                              (row.rank_absolute as number) <= 3 ? 'text-green-600' :
                              (row.rank_absolute as number) <= 10 ? 'text-blue-600' :
                              'text-muted-foreground'
                            }`}>{row.rank_absolute as number}</span>
                          ) : col.key.endsWith('_score') ? (
                            (() => {
                              const score = Number(row[col.key]) || 0;
                              if (!row[col.key] && row[col.key] !== 0) return <span className="text-muted-foreground">—</span>;
                              const color = score >= 80 ? 'text-green-600' : score >= 50 ? 'text-yellow-600' : 'text-red-600';
                              return <span className={`font-bold ${color}`}>{score}</span>;
                            })()
                          ) : col.key.startsWith('keyword_in_') ? (
                            row[col.key] == null ? <span className="text-muted-foreground">—</span> :
                            row[col.key] ? <span className="text-green-600 font-bold">✓</span> : <span className="text-red-400">✗</span>
                          ) : col.key.startsWith('issues_') && col.key.endsWith('_count') ? (
                            (() => {
                              const count = Number(row[col.key]) || 0;
                              if (!row[col.key] && row[col.key] !== 0) return <span className="text-muted-foreground">—</span>;
                              const color = count === 0 ? 'text-green-600' : count <= 3 ? 'text-yellow-600' : 'text-red-600';
                              return <span className={`font-bold ${color}`}>{count}</span>;
                            })()
                          ) : col.key === 'http_status_code' ? (
                            (() => {
                              const code = Number(row[col.key]);
                              if (!code) return <span className="text-muted-foreground">—</span>;
                              const color = code === 200 ? 'text-green-600' : code >= 300 && code < 400 ? 'text-yellow-600' : 'text-red-600';
                              return <span className={`font-bold ${color}`}>{code}</span>;
                            })()
                          ) : col.key === 'response_time_ms' ? (
                            (() => {
                              const ms = Number(row[col.key]);
                              if (!row[col.key] && row[col.key] !== 0) return <span className="text-muted-foreground">—</span>;
                              const color = ms < 1000 ? 'text-green-600' : ms < 3000 ? 'text-yellow-600' : 'text-red-600';
                              return <span className={color}>{ms}ms</span>;
                            })()
                          ) : (col.key.startsWith('has_') || col.key === 'indexable') ? (
                            row[col.key] == null ? <span className="text-muted-foreground">—</span> :
                            row[col.key] ? <span className="text-green-600 font-bold">✓</span> : <span className="text-red-400">✗</span>
                          ) : col.key.endsWith('_bytes') ? (
                            (() => {
                              const val = Number(row[col.key]);
                              if (!row[col.key] && row[col.key] !== 0) return <span className="text-muted-foreground">—</span>;
                              return <span>{Math.round(val / 1024)} KB</span>;
                            })()
                          ) : col.key === 'text_html_ratio' ? (
                            (() => {
                              const val = Number(row[col.key]);
                              if (!row[col.key] && row[col.key] !== 0) return <span className="text-muted-foreground">—</span>;
                              return <span>{(val * 100).toFixed(1)}%</span>;
                            })()
                          ) : col.key === 'image_ALL' ? (
                            (() => {
                              const raw = row[col.key] as string;
                              if (!raw) return <span className="text-muted-foreground">—</span>;
                              try {
                                const urls: string[] = JSON.parse(raw);
                                if (!urls.length) return <span className="text-muted-foreground">0 images</span>;
                                return (
                                  <div className="flex gap-1 flex-wrap max-w-[300px]">
                                    {urls.slice(0, 6).map((src, idx) => (
                                      <img
                                        key={idx}
                                        src={src}
                                        alt=""
                                        className="w-8 h-8 object-cover rounded border cursor-pointer hover:ring-2 hover:ring-primary"
                                        onClick={() => window.open(src, '_blank')}
                                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                      />
                                    ))}
                                    {urls.length > 6 && (
                                      <span className="text-[10px] text-muted-foreground self-center">+{urls.length - 6}</span>
                                    )}
                                  </div>
                                );
                              } catch {
                                return <span className="text-muted-foreground text-xs">{truncate(raw, 60)}</span>;
                              }
                            })()
                          ) : (
                            <span className="whitespace-pre-line" title={String(row[col.key] ?? '')}>
                              {truncate(row[col.key])}
                            </span>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Alibaba Column types ──

interface AlibabaColumnDef {
  key: string;
  label: string;
  group: string;
  coverage?: number;
  total?: number;
  pct?: number;
}

const FIXED_COLUMNS: AlibabaColumnDef[] = [
  { key: 'position', label: '# Position', group: '基本 Basic' },
  { key: 'image_thumb', label: '圖片 Image', group: '基本 Basic' },
  { key: 'title', label: '標題 Title', group: '基本 Basic' },
  { key: 'company', label: '公司 Company', group: '基本 Basic' },
  { key: 'company_country', label: '國家 Country', group: '基本 Basic' },
  { key: 'company_years', label: '年資 Years', group: '基本 Basic' },
  { key: 'price_current', label: '價格 Price', group: '基本 Basic' },
  { key: 'moq_text', label: 'MOQ', group: '基本 Basic' },
  { key: 'has_trade_assurance', label: 'TA', group: '基本 Basic' },
  { key: 'brand', label: '品牌 Brand', group: '基本 Basic' },
  { key: 'rating', label: '評分 Rating', group: '基本 Basic' },
  { key: 'url', label: 'URL', group: '基本 Basic' },
  { key: 'data_level', label: '資料層級 Level', group: '基本 Basic' },
  { key: 'parse_status', label: '解析狀態 Status', group: '基本 Basic' },
  { key: 'attr_count', label: '屬性數 Attrs', group: '基本 Basic' },
];

// Columns that are NOT selected by default
const DEFAULT_UNCHECKED = new Set(['rating']);

const IMAGE_COLUMNS: AlibabaColumnDef[] = [
  { key: 'image_1', label: '圖片1 Image 1', group: '圖片 Images' },
  { key: 'image_2', label: '圖片2 Image 2', group: '圖片 Images' },
  { key: 'image_3', label: '圖片3 Image 3', group: '圖片 Images' },
  { key: 'image_4', label: '圖片4 Image 4', group: '圖片 Images' },
  { key: 'image_5', label: '圖片5 Image 5', group: '圖片 Images' },
  { key: 'image_6', label: '圖片6 Image 6', group: '圖片 Images' },
];

// Alibaba cell renderer
function renderAlibabaCell(col: AlibabaColumnDef, row: Record<string, unknown>) {
  const val = row[col.key];

  // Image columns → <img> thumbnail
  if (col.key === 'image_thumb' || col.key.startsWith('image_')) {
    if (!val) return <span className="text-muted-foreground">—</span>;
    return (
      <img
        src={String(val)}
        alt=""
        className="w-12 h-12 object-cover rounded cursor-pointer"
        onClick={() => window.open(String(val), '_blank')}
      />
    );
  }

  // TA → badge
  if (col.key === 'has_trade_assurance') {
    return val ? <Badge className="bg-green-500 text-white text-[10px]">TA</Badge> : <span className="text-muted-foreground">—</span>;
  }

  // URL → clickable link
  if (col.key === 'url') {
    if (!val) return <span className="text-muted-foreground">—</span>;
    return (
      <a href={String(val)} target="_blank" rel="noopener noreferrer"
        className="text-primary hover:underline text-xs truncate max-w-[150px] block">
        {String(val || '').replace(/^https?:\/\//, '').slice(0, 40)}...
      </a>
    );
  }

  // data_level → badge
  if (col.key === 'data_level') {
    if (val === 'A+B') return <Badge className="bg-blue-500 text-white text-[10px]">A+B</Badge>;
    if (val === 'A') return <Badge variant="outline" className="text-[10px]">A</Badge>;
    return <span className="text-muted-foreground">—</span>;
  }

  // parse_status → badge
  if (col.key === 'parse_status') {
    if (val === 'parsed') return <Badge className="bg-green-500 text-white">parsed</Badge>;
    if (val === 'partial') return <Badge className="bg-orange-500 text-white">partial</Badge>;
    if (val === 'failed') return <Badge variant="destructive">failed</Badge>;
    return <span className="text-muted-foreground text-xs">未爬</span>;
  }

  // attr_count → number, grey if 0
  if (col.key === 'attr_count') {
    const num = Number(val) || 0;
    return <span className={num === 0 ? 'text-muted-foreground' : ''}>{num}</span>;
  }

  // General text → truncate
  if (!val && val !== 0) return <span className="text-muted-foreground">—</span>;
  const s = String(val);
  if (s.length > 50) {
    return <span className="truncate block max-w-[200px]" title={s}>{s}</span>;
  }
  return <>{s}</>;
}

// ── Alibaba DataViewer Tab ──

function AlibabaDataViewer() {
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(new Set());
  const [visibleCols, setVisibleCols] = useState<Set<string>>(() => new Set());
  const [colsInitialized, setColsInitialized] = useState(false);
  const [sortKey, setSortKey] = useState<string>('attr_count');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [isReparsing, setIsReparsing] = useState(false);
  const queryClient = useQueryClient();

  const handleReparseAll = async () => {
    setIsReparsing(true);
    try {
      const res = await alibabaApi.reparseAll();
      const d = res.data;
      toast.success(`Schema 補填完成：${d.updated} 筆更新（product_name/price/brand/sku）`);
      queryClient.invalidateQueries({ queryKey: ["alibaba-data-viewer"] });
      queryClient.invalidateQueries({ queryKey: ["alibaba-dv-keywords"] });
    } catch (err) {
      toast.error(`Schema 補填失敗: ${(err as Error).message}`);
    } finally {
      setIsReparsing(false);
    }
  };

  // Fetch keywords
  const { data: keywords = [] } = useQuery({
    queryKey: ["alibaba-dv-keywords"],
    queryFn: async () => {
      const r = await alibabaApi.dataViewerKeywords();
      return r.data || [];
    },
  });

  const keywordOptions: MultiSelectOption[] = useMemo(() =>
    keywords.map(k => ({ value: k.keyword, label: k.keyword, count: k.product_count })),
    [keywords]
  );

  // Build comma-separated keyword param
  const keywordParam = useMemo(() => [...selectedKeywords].join(','), [selectedKeywords]);

  // Fetch merged data — always load; empty keyword = all keywords
  const { data: viewerData, isLoading } = useQuery({
    queryKey: ["alibaba-dv-data", keywordParam],
    queryFn: async () => {
      const r = await alibabaApi.dataViewer({ keyword: keywordParam, limit: 2000 });
      return r.data;
    },
    enabled: true,
  });

  const summary = viewerData?.summary ?? {
    totalProducts: 0, totalCompanies: 0, avgPrice: 0, attrCount: 0,
    levelA: 0, levelAB: 0, parsedOk: 0, parsedPartial: 0, parsedFailed: 0, hasImages: 0,
  };
  const attrColumns = viewerData?.attrColumns ?? [];
  const rows = viewerData?.rows ?? [];

  // Build dynamic attribute columns
  const dynamicColumns: AlibabaColumnDef[] = useMemo(() =>
    attrColumns.map(c => ({
      key: `attr_${c.key}`,
      label: c.key,
      group: '動態屬性 Attributes',
      coverage: c.coverage,
      total: c.total,
      pct: c.pct,
    })),
    [attrColumns]
  );

  // All columns combined
  const allAlibabaColumns = useMemo(
    () => [...FIXED_COLUMNS, ...IMAGE_COLUMNS, ...dynamicColumns],
    [dynamicColumns]
  );

  // Initialize visible columns once data loads
  if (!colsInitialized && dynamicColumns.length > 0) {
    const defaultCols = new Set<string>();
    // Fixed columns (skip unchecked ones like 'rating')
    FIXED_COLUMNS.forEach(c => {
      if (!DEFAULT_UNCHECKED.has(c.key)) defaultCols.add(c.key);
    });
    // Default images: first 3
    defaultCols.add('image_1');
    defaultCols.add('image_2');
    defaultCols.add('image_3');
    // Default: first 10 dynamic attributes
    dynamicColumns.slice(0, 10).forEach(c => defaultCols.add(c.key));
    setVisibleCols(defaultCols);
    setColsInitialized(true);
  }

  // Column toggle
  const toggleCol = (key: string) => {
    setVisibleCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleGroup = (group: string) => {
    const groupCols = allAlibabaColumns.filter(c => c.group === group);
    const allOn = groupCols.every(c => visibleCols.has(c.key));
    setVisibleCols(prev => {
      const next = new Set(prev);
      groupCols.forEach(c => allOn ? next.delete(c.key) : next.add(c.key));
      return next;
    });
  };

  const activeColumns = allAlibabaColumns.filter(c => visibleCols.has(c.key));
  const groups = [...new Set(allAlibabaColumns.map(c => c.group))];

  // Sort rows
  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      const an = typeof av === 'number' ? av : (typeof av === 'string' && av !== '' ? Number(av) : -Infinity);
      const bn = typeof bv === 'number' ? bv : (typeof bv === 'string' && bv !== '' ? Number(bv) : -Infinity);
      if (!isNaN(an) && !isNaN(bn) && an !== -Infinity && bn !== -Infinity) {
        return sortDir === 'desc' ? bn - an : an - bn;
      }
      const sa = String(av); const sb = String(bv);
      return sortDir === 'desc' ? sb.localeCompare(sa) : sa.localeCompare(sb);
    });
  }, [rows, sortKey, sortDir]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  // Export CSV via API (full details with all attributes)
  const handleExportCSV = async () => {
    if (selectedKeywords.size === 0) { toast.warning("請先選擇關鍵字"); return; }
    try {
      const keyword = [...selectedKeywords].join(',');
      await alibabaApi.exportData({ keyword, type: 'full_details', format: 'csv' });
      toast.success("CSV 下載中…");
    } catch {
      toast.error("CSV 匯出失敗");
    }
  };

  // Export Excel (current view columns)
  const handleExportExcel = () => {
    if (rows.length === 0) { toast.warning("沒有資料"); return; }
    const exportRows = rows.map(row => {
      const out: Record<string, unknown> = {};
      activeColumns.forEach(col => {
        out[col.label] = row[col.key] ?? '';
      });
      return out;
    });
    const suffix = selectedKeywords.size === 1 ? [...selectedKeywords][0] : 'alibaba';
    exportToExcel(exportRows, { filename: `alibaba_${suffix}_${new Date().toISOString().split('T')[0]}.xlsx` });
    toast.success("Excel 匯出成功");
  };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <MultiSelectFilter
              label="關鍵字 Keyword"
              options={keywordOptions}
              selected={selectedKeywords}
              onChange={(v) => { setSelectedKeywords(v); setColsInitialized(false); }}
            />
            <Badge variant="secondary" className="ml-auto">
              {rows.length} 筆 / {selectedKeywords.size > 0 ? `${selectedKeywords.size} 個關鍵字已選` : `全部 ${keywords.length} 個關鍵字`}
            </Badge>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings2 className="h-4 w-4 mr-1.5" />
                  欄位 ({activeColumns.length}/{allAlibabaColumns.length})
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80" align="end">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">顯示欄位</p>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-6 text-xs"
                        onClick={() => setVisibleCols(new Set(allAlibabaColumns.map(c => c.key)))}>全選</Button>
                      <Button variant="ghost" size="sm" className="h-6 text-xs"
                        onClick={() => setVisibleCols(new Set())}>清除</Button>
                    </div>
                  </div>
                  <ScrollArea className="h-[400px]">
                    {groups.map(group => {
                      const groupCols = allAlibabaColumns.filter(c => c.group === group);
                      const allOn = groupCols.length > 0 && groupCols.every(c => visibleCols.has(c.key));
                      return (
                        <div key={group} className="mb-3">
                          <div className="flex items-center gap-2 mb-1">
                            <Checkbox checked={allOn} onCheckedChange={() => toggleGroup(group)} id={`ali-group-${group}`} />
                            <label htmlFor={`ali-group-${group}`} className="text-xs font-semibold text-muted-foreground uppercase">
                              {group}
                            </label>
                          </div>
                          {groupCols.map(col => (
                            <div key={col.key} className="flex items-center gap-2 ml-4 py-0.5">
                              <Checkbox checked={visibleCols.has(col.key)} onCheckedChange={() => toggleCol(col.key)} id={`ali-col-${col.key}`} />
                              <label htmlFor={`ali-col-${col.key}`} className="text-xs flex-1">{col.label}</label>
                              {col.coverage !== undefined && (
                                <span className="text-[10px] text-muted-foreground">{col.coverage}/{col.total}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </ScrollArea>
                </div>
              </PopoverContent>
            </Popover>
            <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={selectedKeywords.size === 0}>
              <Download className="h-4 w-4 mr-1.5" />
              下載 CSV（含全部屬性）
            </Button>
            <Button variant="ghost" size="sm" onClick={handleExportExcel} disabled={rows.length === 0}>
              <Download className="h-4 w-4 mr-1.5" />
              Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-blue-600 border-blue-200 hover:bg-blue-50"
              disabled={isReparsing}
              onClick={handleReparseAll}
            >
              {isReparsing ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1.5" />
              )}
              Schema 補填
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary cards — always visible */}
      {(selectedKeywords.size > 0 || rows.length > 0) && (
        <div className="grid gap-4 md:grid-cols-6">
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <div className="text-2xl font-bold">{summary.totalProducts}</div>
              <div className="text-xs text-muted-foreground">產品數 Products</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <div className="text-2xl font-bold">{summary.totalCompanies}</div>
              <div className="text-xs text-muted-foreground">廠商數 Companies</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <div className="text-2xl font-bold">${(summary.avgPrice ?? 0).toFixed(2)}</div>
              <div className="text-xs text-muted-foreground">均價 Avg Price</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <div className="text-2xl font-bold">{summary.attrCount}</div>
              <div className="text-xs text-muted-foreground">屬性數 Attributes</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <div className={`text-2xl font-bold ${
                summary.totalProducts > 0
                  ? (summary.parsedOk + summary.parsedPartial) / summary.totalProducts >= 0.8 ? 'text-green-600'
                  : (summary.parsedOk + summary.parsedPartial) / summary.totalProducts >= 0.5 ? 'text-yellow-600'
                  : 'text-red-600'
                  : ''
              }`}>
                {summary.parsedOk + summary.parsedPartial}/{summary.totalProducts}
              </div>
              <div className="text-xs text-muted-foreground">已解析 Parsed</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4 text-center">
              <div className={`text-2xl font-bold ${summary.parsedFailed > 0 ? 'text-red-600' : ''}`}>
                {summary.parsedFailed}/{summary.parsedOk + summary.parsedPartial + summary.parsedFailed || 0}
              </div>
              <div className="text-xs text-muted-foreground">失敗 Failed</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Completeness dashboard — always visible */}
      <AlibabaCompleteness />

      {/* Data table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center text-muted-foreground py-16">
              <p>無資料</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {activeColumns.map(col => (
                      <TableHead
                        key={col.key}
                        className="whitespace-nowrap text-xs cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort(col.key)}
                      >
                        <span>{col.label}</span>
                        {sortKey === col.key && (
                          <span className="ml-1 text-[10px]">{sortDir === 'desc' ? '▼' : '▲'}</span>
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRows.map((row, idx) => (
                    <TableRow key={idx}>
                      {activeColumns.map(col => (
                        <TableCell key={col.key} className="text-xs">
                          {renderAlibabaCell(col, row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Main DataViewer Page ──

export default function DataViewer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (['serp','alibaba'] as const).includes(searchParams.get('tab') as 'serp') ? searchParams.get('tab') as string : 'serp';
  const setTab = (t: string) => setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('tab', t); return p; }, { replace: true });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">資料檢視</h1>
        <p className="text-muted-foreground">SERP + HTML 解析完整資料瀏覽，可勾選欄位</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="serp">SERP</TabsTrigger>
          <TabsTrigger value="alibaba">阿里巴巴 Alibaba</TabsTrigger>
        </TabsList>
        <TabsContent value="serp">
          <SerpDataViewer />
        </TabsContent>
        <TabsContent value="alibaba">
          <AlibabaDataViewer />
        </TabsContent>
      </Tabs>
    </div>
  );
}
